import React, { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PinPad, Wordmark } from "@/components/Brand";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const IDLE_MS = 5 * 60 * 1000;

export default function IdleGuard({ username, children }) {
  const [locked, setLocked] = useState(false);
  const timer = useRef(null);

  const reset = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setLocked(true), IDLE_MS);
  };

  useEffect(() => {
    const evs = ["mousedown", "keydown", "touchstart", "scroll"];
    const handler = () => { if (!locked) reset(); };
    evs.forEach((e) => window.addEventListener(e, handler));
    reset();
    return () => { evs.forEach((e) => window.removeEventListener(e, handler)); clearTimeout(timer.current); };
  }, [locked]);

  const unlock = async (pin) => {
    try {
      await api.post("/auth/counter-verify", { username, pin });
      toast.success("Unlocked");
      setLocked(false);
      reset();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <>
      {children}
      {locked && (
        <div className="fixed inset-0 z-50 bg-[color:var(--cream)]/95 backdrop-blur-xl flex items-center justify-center p-6" data-testid="idle-lock">
          <div className="card w-full max-w-md p-8 text-center fadeup">
            <div className="w-14 h-14 rounded-full bg-[color:var(--olive-light)] flex items-center justify-center mx-auto mb-4">
              <Lock className="text-[color:var(--olive)]" />
            </div>
            <Wordmark className="mb-2" sub={false} />
            <h2 className="font-serif text-2xl mb-1">Session Paused</h2>
            <p className="text-sm text-[color:var(--ink-2)] mb-6">Idle for 5 minutes. Re-enter your PIN to resume. Your count is safe.</p>
            <div className="text-xs text-[color:var(--muted)] mb-4">{username}</div>
            <PinPad testid="idle-pinpad" onComplete={unlock} />
          </div>
        </div>
      )}
    </>
  );
}
